import React, { createContext, useContext, useState, useEffect } from 'react'

interface User {
  userId: string
  name: string
  email: string
  username?: string
  roleName: string
  roleLabel: string
  permissions: Record<string, boolean>
  mustChangePassword?: boolean
  onboardingCompleted?: boolean
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (token: string, user: User) => void
  logout: () => void
  hasPermission: (key: string) => boolean
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')
    if (storedToken && storedUser) {
      setToken(storedToken)
      setUser(JSON.parse(storedUser))
    }
  }, [])

  const login = (token: string, user: User) => {
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(user))
    setToken(token)
    setUser(user)
  }

  const refreshUser = async () => {
    try {
      const storedToken = localStorage.getItem('token')
      if (!storedToken) return
      const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${storedToken}` } })
      if (res.ok) {
        const data = await res.json()
        const updated = { ...JSON.parse(localStorage.getItem('user') || '{}'), ...data, userId: data.id }
        localStorage.setItem('user', JSON.stringify(updated))
        setUser(updated)
      }
    } catch {}
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    sessionStorage.removeItem('show_splash')
    setToken(null)
    setUser(null)
    window.location.href = '/login'
  }

  const hasPermission = (key: string) => {
    return user?.permissions?.[key] === true
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, hasPermission, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}